/**
 * Common validation rules and constants
 * Defines reusable validation rules for forms and data validation
 */

import type { ValidationRule } from '../types/validation';

// Common validation rules
export const VALIDATION_RULES: Record<string, ValidationRule> = {
  // Required field validation
  REQUIRED: {
    type: 'required' as const,
    message: 'Ce champ est obligatoire'
  },

  // Email validation
  EMAIL: {
    type: 'email' as const,
    message: 'Veuillez saisir une adresse email valide'
  },

  // Text length validations
  MIN_LENGTH_2: {
    type: 'minLength' as const,
    value: 2,
    message: 'Minimum 2 caractères requis'
  },

  MIN_LENGTH_3: {
    type: 'minLength' as const,
    value: 3,
    message: 'Minimum 3 caractères requis'
  },

  MAX_LENGTH_50: {
    type: 'maxLength' as const,
    value: 50,
    message: 'Maximum 50 caractères autorisés'
  },

  MAX_LENGTH_100: {
    type: 'maxLength' as const,
    value: 100,
    message: 'Maximum 100 caractères autorisés'
  },

  // Date validations
  FUTURE_DATE: {
    type: 'futureDate' as const,
    message: 'La date doit être dans le futur'
  },

  PAST_DATE: {
    type: 'pastDate' as const,
    message: 'La date doit être dans le passé'
  },

  // Number validations
  MIN_PASSENGERS: {
    type: 'min' as const,
    value: 1,
    message: 'Au moins 1 voyageur requis'
  },

  MAX_PASSENGERS: {
    type: 'max' as const,
    value: 9,
    message: 'Maximum 9 voyageurs autorisés'
  },

  // Pattern validations
  PHONE_PATTERN: {
    type: 'pattern' as const,
    value: /^(\+33|0)[1-9](\d{8})$/,
    message: 'Numéro de téléphone français invalide'
  },

  POSTAL_CODE_PATTERN: {
    type: 'pattern' as const,
    value: /^\d{5}$/,
    message: 'Code postal invalide (5 chiffres requis)'
  }
} as const;

// Form-specific validation rule sets
export const FORM_VALIDATION_RULES = {
  SEARCH_FORM: {
    destination: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_LENGTH_2],
    departureDate: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.FUTURE_DATE],
    passengers: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_PASSENGERS, VALIDATION_RULES.MAX_PASSENGERS]
  },

  CONTACT_FORM: {
    name: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_LENGTH_2, VALIDATION_RULES.MAX_LENGTH_50],
    email: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.EMAIL],
    phone: [VALIDATION_RULES.PHONE_PATTERN],
    message: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_LENGTH_3, VALIDATION_RULES.MAX_LENGTH_100]
  },

  BOOKING_FORM: {
    firstName: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_LENGTH_2, VALIDATION_RULES.MAX_LENGTH_50],
    lastName: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.MIN_LENGTH_2, VALIDATION_RULES.MAX_LENGTH_50],
    email: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.EMAIL],
    phone: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.PHONE_PATTERN],
    birthDate: [VALIDATION_RULES.REQUIRED, VALIDATION_RULES.PAST_DATE]
  }
} as const;

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Erreur de connexion. Veuillez réessayer.',
  SERVER_ERROR: 'Erreur serveur. Veuillez réessayer plus tard.',
  VALIDATION_ERROR: 'Veuillez corriger les erreurs dans le formulaire.',
  NOT_FOUND: 'La page demandée n\'a pas été trouvée.',
  UNAUTHORIZED: 'Accès non autorisé.',
  FORBIDDEN: 'Accès interdit.',
  TIMEOUT: 'Délai d\'attente dépassé. Veuillez réessayer.',
  GENERIC_ERROR: 'Une erreur inattendue s\'est produite.'
} as const;

// Success messages
export const SUCCESS_MESSAGES = {
  FORM_SUBMITTED: 'Formulaire envoyé avec succès.',
  SEARCH_COMPLETED: 'Recherche effectuée avec succès.',
  BOOKING_CONFIRMED: 'Réservation confirmée.',
  EMAIL_SENT: 'Email envoyé avec succès.',
  DATA_SAVED: 'Données sauvegardées avec succès.'
} as const;